import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '@/hooks/useIsMobile';
import { getWhatsAppHref } from '@/lib/whatsapp';
import { trackWhatsAppClick, type WhatsAppClickSource } from '@/lib/whatsapp-tracking';

import { FloatingCTAButton } from './FloatingCTAButton';
import { HandshakeIcon } from './HandshakeIcon';
import { WhatsAppIcon } from './WhatsAppIcon';

import './FloatingCTAActions.css';

// Hide both buttons — auth/admin chrome where floating CTAs would distract.
// Spec V1.1 §6.1 lists the WhatsApp masking set; Sell mirrors it.
const HIDDEN_BOTH = [
  '/signin',
  '/signup',
  '/verify',
  '/verify/*',
  '/reset-password',
  '/reset-password/*',
  '/admin',
  '/admin/*',
] as const;

// Self-link prevention for Sell only.
const HIDDEN_SELL_ONLY = ['/sell'] as const;

// Routes with the fixed mobile bottom bar (UB4). On mobile the bar takes over
// the WhatsApp CTA, and the remaining Sell CTA is lifted above the 56px bar.
const MOBILE_BOTTOM_BAR_ROUTES = ['/deals/:slug'] as const;

export type FloatingCTAVariant = 'public' | 'whatsapp-only';

function derivePageSource(pathname: string): WhatsAppClickSource {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/deals/')) return 'deal';
  if (pathname === '/about') return 'about';
  if (pathname === '/faq') return 'faq';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/sell') return 'sell';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  return 'other';
}

export function FloatingCTAActions({ variant = 'public' }: { variant?: FloatingCTAVariant }) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();

  const matchesAny = (patterns: readonly string[]) =>
    patterns.some((pattern) => matchPath(pattern, location.pathname));

  // Shown immediately (client 2026-06-01): no 5 s appear delay, no scroll gate.
  // Only suppressed on the auth/admin chrome routes listed below.
  if (matchesAny(HIDDEN_BOTH)) return null;

  const mobileBottomBar = isMobile && matchesAny(MOBILE_BOTTOM_BAR_ROUTES);
  const showSell = variant === 'public' && !matchesAny(HIDDEN_SELL_ONLY);
  const showWhatsApp = !mobileBottomBar;

  if (!showSell && !showWhatsApp) return null;

  return (
    <div
      className={
        mobileBottomBar
          ? 'floating-cta-actions floating-cta-actions--above-bar'
          : 'floating-cta-actions'
      }
      data-testid="floating-cta-actions"
    >
      {showSell && (
        <FloatingCTAButton
          to="/sell"
          label={t('floatingSellCta.label', 'Sell with us')}
          icon={<HandshakeIcon />}
          testId="floating-sell-cta"
          isMobile={isMobile}
        />
      )}
      {showWhatsApp && (
        <FloatingCTAButton
          href={getWhatsAppHref()}
          label={t('floatingWhatsApp.label', 'Contact agency')}
          icon={<WhatsAppIcon />}
          testId="floating-whatsapp"
          isMobile={isMobile}
          onClick={() =>
            trackWhatsAppClick({
              pageSource: derivePageSource(location.pathname),
              language: i18n.language?.split('-')[0] ?? 'en',
            })
          }
        />
      )}
    </div>
  );
}
